import {
  slab, wallX, wallZ, goal, shard, sign, checkpoint, edgeLights, neon, deco,
  gravfield, wind, lift, boost, shatter,
} from '../kit.js';

// An expedition across a district, with mandatory destinations and free
// routes between them. Floors stay non-portalable outside the dive wells:
// the lit high bands are launches, not accidental exits at ankle height.
const roof = (x, y, z, w, d, color = 'cyan') => [
  slab(x, y, z, w, d, 'plate', 1.5),
  ...edgeLights(x, y, z, w, d, color),
  // Silhouette below the playable roof; decorative, no hidden colliders.
  deco(x, y - 38, z, [w - 2, 72, d - 2], 'trim'),
];
const station = (x, y, z) => [
  // A flight can approach a signal from either side. Co-locate the save
  // point with its pickup so claiming it always banks the same landing.
  shard(x, y + 1.6, z), checkpoint(x, y, z, 0),
  neon(x, y + 0.08, z, [5, 0.12, 5], 'lime'),
  neon(x, y + 8, z, [0.12, 8, 0.12], 'lime'),
];
const well = (x, top, lip, depth = 62, length = 36) => {
  const bottom = top - depth;
  const z = lip - length / 2;
  return [
    slab(x, bottom, z, 34, length, 'panel', 1.5),
    // The near wall tucks under the lip. The far wall ends well BELOW
    // the launch band, leaving both the downward shot and the flight clear.
    wallZ(x, bottom, lip + 0.75, 36, depth, 'plate', 1.5),
    wallZ(x, bottom, lip - length - 0.75, 36, depth - 26, 'plate', 1.5),
    wallX(x - 17.75, bottom, z, length, depth, 'plate', 1.5),
    wallX(x + 17.75, bottom, z, length, depth, 'plate', 1.5),
    neon(x, bottom + 0.1, z, [28, 0.12, 0.18], 'magenta'),
  ];
};
const launch = (x, y, z) => [
  wallZ(x, y + 6, z, 26, 18, 'panel', 1.5),
  neon(x, y + 6.2, z - 0.8, [25, 0.15, 0.15], 'cyan'),
  neon(x, y + 23.8, z - 0.8, [25, 0.15, 0.15], 'cyan'),
];

export default {
  id: 'skybreak',
  name: 'SKYBREAK',
  blurb: 'Eight signals across the skyline. Bring them home.',
  mode: { type: 'collect', par: 240 },
  redline: { seconds: 216 },
  // Branch off the wind course. Visiting PHOSPHOR is not a prerequisite.
  unlockAfter: 'slipstream',
  spawn: { p: [0, 180.4, 612], yaw: 0 },
  killY: -65,
  env: {
    fog: 0x0b1829, fogDensity: 0.0015,
    sky: { zenith: 0x030b20, horizon: 0x243a60, glow: 0xe07042, aurora: 0x65dfff },
    floorY: -180,
  },
  objects: [
    // 1. THE ROOFLINE. Two hops introduce the district's roof edges before
    // the first big fling. Wide overlapping X spans allow a forgiving line.
    ...roof(0, 180, 0, 24, 40),
    ...roof(10, 180, -48, 24, 44),
    ...roof(0, 180, -100, 34, 48),
    ...station(0, 180, -100),
    sign(0, 184, 6, 0, ['SKYBREAK', 'EIGHT SHARDS. ONE DISTRICT.', 'FOLLOW THE LIME SIGNALS']),
    sign(0, 183.4, -12, 0, ['01 // THE ROOFLINE', 'SPRINT AND HOP THE ROOFS'], '#65dfff'),
    sign(0, 183.4, -118, 0, ['THE FIRST FLIGHT', 'TAG THE HIGH WALL BEHIND YOU', 'DROP, THEN TAG THE FLOOR']),
    ...launch(0, 180, -112),
    ...well(0, 180, -124),

    // 2. CROSSWIND. A broad landing feeds a side balcony, then a second
    // fling bends east. The current starts AFTER the well, never on the
    // falling entry. The left signal makes landing here part of the run.
    ...roof(0, 120, -260, 50, 96),
    ...station(-16, 120, -270),
    sign(0, 124, -278, 0, ['02 // CROSSWIND', 'SIGNAL LEFT. NEXT WELL RIGHT.', 'THE WIND BENDS YOUR FLIGHT'], '#ffb020'),
    ...roof(16, 120, -302, 72, 12, 'amber'),
    ...roof(32, 120, -334, 30, 56, 'amber'),
    checkpoint(32, 120, -342, 0),
    ...launch(32, 120, -350),
    ...well(32, 120, -362),
    wind(44, 76, -436, 70, 78, 64, [1, 0, 0], 14, 16),
    ...roof(58, 60, -490, 60, 72, 'lime'),
    ...station(58, 60, -496),

    // 3. THE HANGING GARDENS. The field includes the takeoff deck and all
    // hop apices. Offset islands overlap laterally, so walking-speed hops
    // have a line as well as the more direct sprinting route.
    sign(58, 64, -510, 0, ['03 // HANGING GARDENS', 'JUMP. FLOAT. STEER.', 'A SIGNAL WAITS ON THE SECOND ROOF'], '#b28aff'),
    gravfield(58, 42, -610, 100, 58, 184, 0.28),
    ...roof(66, 60, -553, 24, 26, 'magenta'),
    ...roof(50, 60, -593, 24, 26, 'magenta'),
    ...station(50, 60, -593),
    ...roof(66, 60, -633, 24, 26, 'magenta'),
    ...roof(50, 60, -673, 24, 26, 'magenta'),
    ...roof(58, 60, -730, 50, 80),
    ...station(58, 60, -726),

    // 4. UPDRAFT. Leave the lift sideways above its rim, then follow the
    // exposed dogleg to the next signal. The upper deck never roofs the
    // lift, and every bridge joins its neighbor on the same plane.
    sign(58, 64, -738, 0, ['04 // UPDRAFT', 'RIDE THE LIGHT', 'STEP RIGHT AT THE TOP']),
    lift(58, 60, -752, 38, 3),
    ...roof(79, 92, -752, 30, 26),
    checkpoint(78, 92, -752, 0),
    ...roof(79, 92, -786, 14, 42),
    ...roof(98, 92, -804, 50, 12),
    ...roof(116, 92, -833, 14, 46),
    ...roof(116, 92, -891, 30, 58),
    sign(116, 95.4, -844, 0, ['MIND THE GAP', 'KEEP YOUR JUMP'], '#ffb020'),
    ...station(116, 92, -902),

    // 5. THE GLASS RUN. Left is the strip-fed speed line; the right lane
    // lets a careful courier keep moving. Both deliver the same signal.
    ...roof(116, 92, -972, 38, 104, 'amber'),
    sign(116, 95.6, -928, 0, ['05 // THE GLASS RUN', 'LEFT: SPEED THROUGH GLASS', 'RIGHT: THE OPEN BALCONY'], '#ffb020'),
    boost(108, 92, -946, 7, 32, [0, 0, -1], 44, 38),
    shatter(108, 94.5, -960, [12, 5, 0.4], 24),
    boost(108, 92, -982, 7, 26, [0, 0, -1], 44, 38),
    shatter(108, 94.5, -992, [12, 5, 0.4], 26),
    ...station(116, 92, -1008),
    checkpoint(116, 92, -1018, 0),

    // 6. SKYBREAK. Glass changes from walls to floors. The only portalable
    // entry is below the panes; the last pane leaves an aiming window
    // above the floor. The launch band is a hundred metres ABOVE the
    // departure roof: the dive buys a return to the skyline.
    sign(116, 95.6, -1016, 0, ['06 // SKYBREAK', 'TAG THE HIGH BLUE BAND BEHIND YOU', 'DIVE THROUGH GLASS. TAG THE FLOOR.']),
    ...well(116, 92, -1024, 112, 44),
    shatter(116, 70, -1046, [32, 0.4, 40], 20),
    shatter(116, 50, -1046, [32, 0.4, 40], 36),
    shatter(116, 30, -1046, [32, 0.4, 40], 50),
    ...launch(116, 182, -1012),
    deco(98, 145, -1012, [4, 110, 4], 'trim'),
    deco(134, 145, -1012, [4, 110, 4], 'trim'),
    ...roof(116, 170, -1160, 64, 130, 'lime'),
    ...station(116, 170, -1170),
    sign(116, 174, -1186, 0, ['THE SKYLINE IS YOURS', 'BRING EVERY SIGNAL HOME'], '#9dff3c'),
    goal(116, 170, -1202),
  // Centre the 1.2km district inside the existing +/-800m ghost envelope.
  // Authoring coordinates above remain distance north from the first roof.
  ].map(o => ({ ...o, p: [o.p[0], o.p[1], o.p[2] + 600] })),
};
